import { createSignal } from "solid-js";

type ParamValue = string | undefined;

/**
 * This hook provides a reactive interface to the URLSearchParams object of the current URL.
 * You should use this instead of the @solid/router hook because it does depend on the router which we do
 * not want.
 * @returns
 */
export function useSearchParams<T extends Record<string, ParamValue>, K extends keyof T = keyof T>() {
	const [searchParams, mutateSearchParams] = createSignal<URLSearchParams>(
		new URLSearchParams(window.location.search),
	);

	window.addEventListener("popstate", () => {
		mutateSearchParams(new URLSearchParams(window.location.search));
	});

	function setSearchParams(arg1: K, arg2: ParamValue): void;
	function setSearchParams(arg1: K[], arg2?: ParamValue): void;
	function setSearchParams(arg1: Record<K, ParamValue>): void;
	function setSearchParams(arg1: K | K[] | Partial<Record<K, ParamValue>>, arg2?: ParamValue) {
		const url = new URL(window.location.href);

		const handle = (key: K, value: ParamValue) => {
			if (value !== undefined) {
				url.searchParams.set(key as string, value);
			} else {
				url.searchParams.delete(key as string);
			}
		};

		if (Array.isArray(arg1)) {
			for (const key of arg1) {
				handle(key, arg2);
			}
		} else if (typeof arg1 === "object") {
			for (const key in arg1) {
				handle(key as K, arg1[key]);
			}
		} else if (typeof arg1 === "string") {
			handle(arg1, arg2);
		}

		window.history.pushState({}, "", url);
		mutateSearchParams(url.searchParams);
	}

	const get = (key: K): string | null => {
		return searchParams().get(key as string) || null;
	};

	const has = (key: K): boolean => {
		return searchParams().has(key as string);
	};

	const remove = (key: K): void => {
		setSearchParams(key, undefined);
	};

	const set = (key: K, value: ParamValue): void => {
		setSearchParams(key, value);
	};

	return [{ get, has, remove, set }, setSearchParams] as const;
}
